import {Driver} from "@/models/driver"
import {ColumnDef} from "@tanstack/react-table"
import {useEffect, useState} from "react"
import {useDebounce} from "@/hooks/use-debounce"
import {DataTable} from "@/components/data-table.tsx";

interface DriverDataTableProps {
    columns: ColumnDef<Driver, any>[]
    data: Driver[]
    searchTerm?: string
}

export function DriverDataTable({columns, data, searchTerm = ""}: DriverDataTableProps) {
    const [filteredData, setFilteredData] = useState<Driver[]>(data)

    const debouncedSearchTerm = useDebounce(searchTerm, 300)

    useEffect(() => {
            if (!debouncedSearchTerm.trim()) {
                setFilteredData(data)
                return
            }

            const filtered = data.filter(driver => {
                const searchLower = debouncedSearchTerm.toLowerCase()

                const memberNoMatch = driver.memberNo.toString().includes(debouncedSearchTerm)
                const nameMatch = `${driver.firstName} ${driver.lastName}`
                    .toLowerCase()
                    .includes(searchLower)
                const addressMatch = `${driver.street} ${driver.houseNumber} ${driver.postcode} ${driver.city}`
                    .toLowerCase()
                    .includes(searchLower)
                const phoneNumbersMatch = driver.phoneNumbers.some(phone =>
                    phone.number.includes(debouncedSearchTerm)
                )
                const commentsMatch = driver.availabilityComments
                    ?.toLowerCase()
                    .includes(searchLower)
                const currentNotesMatch = driver.currentNotes
                    ?.toLowerCase()
                    .includes(searchLower)
                const emailMatch = driver.email
                    ?.toLowerCase()
                    .includes(searchLower)
                const districtMatch = driver.district
                    ?.toLowerCase()
                    .includes(searchLower)

                return memberNoMatch || nameMatch || addressMatch || phoneNumbersMatch || commentsMatch
                    || currentNotesMatch || emailMatch || districtMatch
            })

            setFilteredData(filtered)
        }, [debouncedSearchTerm, data]
    )

    return (
        <div className="space-y-4">
            <DataTable
                columns={columns}
                data={filteredData}
                resetPageKey={debouncedSearchTerm}
                data-testid="drivers-table"
            />
        </div>
    )
}
